import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Close, VButton, VFlex, VInput, VStack } from '@/package';
import { useAuthStore, useTenantStore } from '@/store';

export const RemoveWorkSpace = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();
  const { signOutTenant } = useAuthStore();
  const { getCurrentTenant } = useTenantStore();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const tenant = getCurrentTenant();

  const onTenantDelete = () => {
    if (!tenant?.tenantID) return;

    fetch(
      `${import.meta.env.VITE_APP_CONTROL_PLANE_URL}/tenant/${tenant?.tenantID}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      },
    )
      .then(() => {
        signOutTenant();
        navigate('/auth/offboarding');
      })
      .catch(err => {
        console.warn(err);
        setErrorMessage('Something went wrong. Please try again.');
      });
  };

  return (
    <VStack gap={1.5} className="w-full">
      <VFlex className="w-full" items="center" justify="between">
        <div className="text-headline-md font-bold">Delete workspace</div>
        <Close
          className="h-6 w-6 text-gray-400"
          role="button"
          onClick={close}
        />
      </VFlex>

      <div className="text-body-xl text-error-500">
        Are you sure you want to delete your{' '}
        <span className="font-bold text-primary-500">
          {tenant?.tenantName ?? 'VueX'}
        </span>{' '}
        workspace?
      </div>

      <VStack gap={1} className="w-full">
        <VFlex className="h-[445px] w-full" justify="between">
          <VStack
            className="h-full w-[624px] rounded-md border border-gray-200 p-2"
            gap={1}
          >
            <VStack gap={0.25}>
              <div className="text-body-md font-semibold">
                Refund Eligibility
              </div>
              <VStack className="text-body-md">
                <span>• A refund is available within 7 days of purchase,</span>
                <span>• Only if the service has not been used at all.</span>
                <span>
                  • No refunds will be issued if there is any usage history or
                  usage-based charges have been incurred.
                </span>
              </VStack>
            </VStack>

            <VStack gap={0.25}>
              <div className="text-body-md font-semibold">
                Grace Period After Cancellation
              </div>

              <VStack className="text-body-md">
                <span>
                  • When you cancel your subscription, a 15-day grace period
                  will be applied.
                </span>
                <span>
                  • During this period, login and service access will be
                  restricted.
                </span>

                <span className="inline-block pl-4">
                  • If you contact us at{' '}
                  <span className="text-blue-300">[support@vueron.com]</span>
                  during the grace period, we’ll assist you with account
                  recovery.
                </span>

                <span>
                  • After the grace period ends, all resources associated with
                  your account will be automatically deleted, and a notification
                  email will be sent once deletion is complete.
                </span>
              </VStack>
            </VStack>

            <VStack gap={0.25}>
              <div className="text-body-md font-semibold">
                Account Recovery Notice
              </div>

              <VStack className="text-body-md">
                <span>
                  • You may re-register using the same account after the grace
                  period,
                </span>
                <span>
                  • However, previous account information and usage history
                  cannot be recovered.
                </span>
              </VStack>
            </VStack>

            <VStack className="text-body-md">
              <div>
                📌 For details on how your personal information is handled,
                please refer to our [Privacy Policy].
              </div>
              <div>
                ❓ For questions regarding cancellations or refunds, contact us
                at
                <span className="text-blue-300">[support@vueron.com ]</span>
              </div>
            </VStack>
          </VStack>

          <VStack className="h-full w-[308px]" justify="between" items="center">
            <VInput
              type="password"
              label={t('auth.confirmPasswordLabel.inspissant')}
              placeholder="Enter"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fluid
              valid={!errorMessage.length}
              comment={errorMessage}
            />

            <div className="text-body-md">
              Clicking 'Delete Workspace' will permanently delete it.
            </div>
          </VStack>
        </VFlex>

        <VFlex justify="between" className="w-full">
          <VButton
            className="w-[308px] capitalize"
            outlined
            variant="secondary"
            onClick={close}
          >
            {t('common.cancel.isocinchonine')}
          </VButton>
          <VButton
            variant="danger"
            className="w-[308px]"
            onClick={() => onTenantDelete()}
          >
            Delete workspace
          </VButton>
        </VFlex>
      </VStack>
    </VStack>
  );
};
